import React, { FC, ReactElement, useContext, useEffect, useRef, useState } from 'react';
import { Grid } from '@mui/material';
import { AppContext } from '../../context/AppContext';
import BakeasyHeaderLabel from '../../components/labels/BakeasyHeaderLabel';
import BakeasyTable from '../../components/tables/BakeasyTable';
import { BakeasyChipEdit } from '../../components/common/BakeasyChip';
import BakeasyEmptyMsg from '../../components/common/BakeasyEmptyMsg';
import { t } from 'i18next';
import { useNavigate } from 'react-router-dom';
import BakeasySwitch from '../../components/common/BakeasySwitch';
import { formatDateTime } from '../../utils/DateUtils';
import '../../styles/styles.css';
import { useAppDispatch, useAppSelector } from '../../redux_store/reduxStore';
import {
  clearSupplierState,
  deactivateSupplier,
  downloadSuppliers,
  fetchSuppliers,
  reactivateSupplier,
  suppliersState
} from '../../redux_store/slices/suppliersSlice';
import { UNAUTHORIZED } from '../../constants/Constants';
import { authActions } from '../../redux_store/slices/authSlice';
import * as TextField from '../../components/textfields/BakeasyTextFields';
import * as Button from '../../components/buttons/BakeasyButtons';
import BakeasyPaper from '../../components/common/BakesasyPaper';
import { supplierTableHeaders } from '../../constants/TableHeaders';
import BakeasySelectedFilters from '../../components/common/BakeasySelectedFilters';
import { deleteKeyFromObject } from '../../utils/commonUtils';
import { useDebouncedFetch } from '../../custom/useDebouncedCallback';

export const Suppliers: FC = (): ReactElement => {
  const tableRef = useRef<any>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const fetchData = useDebouncedFetch();
  let myContext = useContext(AppContext);

  const [search, setSearch] = useState<string>('');
  const [suppliersList, setSuppliersList] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [tFilter, setTFilter] = useState<any>({});

  let { error, value, rowsPerPage, pageNo, filter } = useAppSelector(suppliersState);

  useEffect(() => {
    if ('search' in filter) {
      let value: any = filter?.search;
      setSearch(value);
    }
  }, [filter]);

  useEffect(() => {
    if (error === UNAUTHORIZED) {
      dispatch(authActions.logout());
    }
  }, [error]);

  useEffect(() => {
    if (value?.totalCount === -1) {
      refreshPageData(pageNo);
    } else {
      const filteredData: any = value?.data?.map((supplier: any) => {
        return {
          _id: supplier?._id,
          supplierNumber: supplier?.supplierNumber ?? '',
          supplierName: supplier?.supplierName,
          onSupplierNameClick: () => {
            navigate('/addEditSupplier', {
              state: { isView: true, isEdit: false, _id: supplier?._id }
            });
          },
          contact: supplier?.contact1?.firstName + ' ' + supplier?.contact1?.lastName,
          onContactClick: () => {
            openContactSupplierConfirmDialog(supplier);
          },
          createdDate: supplier?.createdDate ? formatDateTime(supplier?.createdDate) : '',
          lastAccess: supplier?.lastAccess ? formatDateTime(supplier?.lastAccess) : '',
          storeCount: supplier?.storeCount,
          productCount: supplier?.productCount,
          status: (
            <BakeasySwitch
              value={supplier?.status === 'Active' ? true : false}
              onChange={(event: any) => {
                event.stopPropagation();
                supplier?.status === 'Active'
                  ? openDeActivateSupplierConfirmDialog(supplier?._id)
                  : openReActivateSupplierConfirmDialog(supplier?._id);
              }}
            />
          ),
          actions: [
            <BakeasyChipEdit
              key={supplier?._id}
              onClick={(event: any) => {
                event.stopPropagation();
                navigate('/addEditSupplier', { state: { isEdit: true, _id: supplier?._id } });
              }}
            />
          ]
        };
      });

      setSuppliersList(filteredData);
      setCount(value?.totalCount);
    }
  }, [value]);

  // useEffect(() => {
  //   return () => fetchData.cancel(); // Cancel debounce on component unmount to avoid memory leaks
  // }, [fetchData]);

  const refreshPageData = async (newPage: number, search?: string, filter?: {} | undefined) => {
    let payload: any = {
      limit: rowsPerPage,
      page: newPage + 1,
      sortBy: 'createdDate',
      sortDirection: -1
    };

    if (filter != undefined && filter !== '') {
      payload = { ...payload, ...filter };
    }

    payload = { ...payload, page: newPage + 1 };

    if (search !== undefined && search !== '') {
      payload = { ...payload, search };
    }

    if (search === undefined || search === '') {
      delete payload['search'];
    }

    // await fetchData(fetchSuppliers, payload);
    await dispatch(fetchSuppliers(payload));

  };

  function openContactSupplierConfirmDialog(supplier: any) {
    myContext.setConfirmDialog({
      type: 'contact',
      open: true,
      title: t('CONTACT_SUPPLIER'),
      data: supplier?.contact1
    });
  }

  function openReActivateSupplierConfirmDialog(_id: any) {
    myContext.setConfirmDialog({
      type: 'reActivate',
      open: true,
      title: t('REACTIVATE_SUPPLIER'),
      message: t('REACTIVATE_SUPPLIER_MESSAGE'),
      onNoClick: async (event: any) => {},
      onYesClick: async (event: any, param: string | undefined) => {
        await dispatch(reactivateSupplier({ id: _id }));
        await refreshPageData(pageNo - 1);
      }
    });
  }

  function openDeActivateSupplierConfirmDialog(_id: any) {
    myContext.setConfirmDialog({
      type: 'deActivate',
      open: true,
      title: t('DEACTIVATE_SUPPLIER'),
      message: t('DEACTIVATE_SUPPLIER_MESSAGE'),
      onNoClick: async (event: any) => {},
      onYesClick: async (event: any, param: string | undefined) => {
        await dispatch(deactivateSupplier({ id: _id }));
        await refreshPageData(pageNo - 1);
      }
    });
  }

  function openFilterSupplierConfirmDialog() {
    myContext.setConfirmDialog({
      type: 'supplier-filter',
      open: true,
      title: t('SUPPLIER_FILTER'),
      data: filter,
      onNoClick: (event: any) => {},
      onYesClick: async (event: any, param: any) => {
        setTFilter(param);
        await refreshPageData(0, search, param);
      }
    });
  }

  const handleDelete = async (key: any) => {
    // const newFilters = { ...tFilter };
    // delete newFilters[key];
    let newFilters = await deleteKeyFromObject(key, tFilter);
    setTFilter(newFilters);
    refreshPageData(pageNo - 1, search, newFilters);
  };

  function suppliersUI() {
    return (
      <Grid>
        <BakeasyPaper>
          <BakeasyHeaderLabel message={t('SUPPLIERS')} />

          <Grid
            container
            spacing={2}
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Grid item xs={12} sm={12} md={3} lg={3} xl={3}>
              <TextField.Search
                value={search}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setSearch(event?.target?.value);
                  if (event?.target?.value?.length === 0 || event?.target?.value?.length > 2) {
                    refreshPageData(
                      pageNo - 1,
                      event?.target?.value?.length === 0 ? undefined : event?.target?.value,
                      filter
                    );
                  }
                }}
                onIconClick={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
                onEnterClick={(event: React.KeyboardEvent<HTMLInputElement>) => {
                  refreshPageData(pageNo - 1, search, filter);
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={9} lg={6} xl={6}>
              <div className="tableOptions">
                <Button.Print
                  onClick={async function(
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): Promise<void> {
                    await dispatch(downloadSuppliers({ ...filter, downloadType: 'pdf' }));
                  }}
                />
                <Button.Export
                  onClick={async function(
                    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
                  ): Promise<void> {
                    await dispatch(downloadSuppliers({ ...filter, downloadType: 'csv' }));
                  }}
                />

                <Button.Filter
                  onClick={function(event: React.MouseEvent<HTMLButtonElement, MouseEvent>): void {
                    openFilterSupplierConfirmDialog();
                  }}
                />

                <Button.Add
                  onClick={async (event: React.MouseEvent<HTMLButtonElement>) => {
                    dispatch(clearSupplierState());
                    navigate('/addEditSupplier', {
                      state: { isNew: true, isView: false, isEdit: false }
                    });
                  }}
                />
              </div>
            </Grid>
          </Grid>

          <BakeasySelectedFilters filter={filter} onDelete={handleDelete} />

          <Grid>
            {suppliersList?.length > 0 ? (
              <BakeasyTable
                ref={tableRef}
                pageNo={pageNo - 1}
                search={search}
                headers={supplierTableHeaders}
                tableData={suppliersList}
                count={count}
                refreshPageData={refreshPageData}
                filter={filter}
              />
            ) : (
              <BakeasyEmptyMsg message={t('NO_DATA_FOUND')} />
            )}
          </Grid>
        </BakeasyPaper>
      </Grid>
    );
  }

  return suppliersUI();
};
